import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getBookingWidgetRoomData } from 'features/roomsSlice';
import { MobileHeaderOptions } from 'components/mobileHeaderOptions';
import { MobileRoom, SkeletonMobileRoom } from 'components/mobileRoom';
import { useGetBookingData, useScrollToTop } from 'hooks';
import './MobilePropertyDetails.css';
import { LowestRatesSlider } from 'components/lowestRatesSlider';
import { differenceInDays } from 'date-fns';
import { flatten } from 'lodash';
import { setCheckInDay, setCheckOutDay } from 'features/userSlice';

const MobilePropertyDetails = ({
    closeProperyDetails,
    property = {},
    showHeader,
    openPropertyDetails,
    anchor,
}) => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.miniBookingWidget?.data);

    const isFetchingPrices = useSelector(
        (state) => state.miniBookingWidget.isFetching
    );
    const isFetchingRooms = useSelector((state) => state.rooms?.isFetching);
    const roomTypes = useSelector((state) => state.rooms.roomTypes);
    const ref = useScrollToTop([isFetchingPrices, isFetchingRooms]);

    const roomsUrl = data?.bigPlugin?.allRoomsUrl;

    const propertyType = useSelector(
        (state) => state.miniBookingWidget?.data?.propertyType
    );
    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);
    const isCartOpen = useSelector((state) => state.cart?.open);
    useEffect(() => {
        if (roomsUrl) {
            dispatch(getBookingWidgetRoomData({ roomsUrl }));
        }
    }, [roomsUrl, isCartOpen]);

    const userCountryCode =
        useSelector((state) => state.user?.language) || 'en-US';
    const calendar = useSelector(
        (state) => state.miniBookingWidget?.data?.futureDates
    );
    const { getBookingData } = useGetBookingData();
    const onClick = (day) => {
        let checkOutDate = new Date(day?.date);
        const els = document.querySelectorAll('.ab-daily-lowest-rate');
        for (const element of els) {
            element.classList.remove('ab-day-selected');
        }
        const thisEl = document.querySelectorAll(`[data-date="${day?.date}"]`);
        thisEl[0].classList.add('ab-day-selected');
        checkOutDate = checkOutDate.setDate(
            checkOutDate.getDate() + day?.min_stay
        );
        checkOutDate = new Date(checkOutDate);
        const dateString = checkOutDate
            .toLocaleDateString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            })
            .split('/')
            .reverse()
            .join('-');

        const checkOutMonth = checkOutDate.getMonth() + 1;
        const checkOutMontObj = calendar.find((month) => {
            return month.fd_Month === checkOutMonth;
        });

        if (!checkOutMontObj) {
            console.error('Month not found');
            return false;
        }
        const checkOuDayObj = checkOutMontObj?.fd_JsonResult?.data?.days.find(
            (day) => {
                return day.date === dateString;
            }
        );
        if (!checkOuDayObj) {
            console.error('Day not found');
            return false;
        }
        dispatch(setCheckInDay({ day: day }));

        dispatch(setCheckOutDay({ day: checkOuDayObj }));
        getBookingData({
            checkInDate: day?.date,
            checkOutDate: checkOuDayObj?.date,
        });
        return false;
    };
    const initialDate = useSelector((state) => state.user?.initialDate);
    const [lowestRateDays, lowestRateNextDays, lowestRateMonthIndexes] =
        useMemo(() => {
            const filteredNextDays = [];
            const filteredMonthIndexes = [];
            const monthsToDisplay = calendar?.slice(0, 2);
            const filteredDays = monthsToDisplay.map((month, monthIndex) => {
                return month?.fd_JsonResult?.data?.days?.filter(
                    (day, dayIndex, daysArr) => {
                        const today = new Date(initialDate);
                        const thisDay = new Date(day?.date);
                        if (differenceInDays(thisDay, today) >= 0) {
                            const nextDay = daysArr[dayIndex + 1];
                            filteredMonthIndexes.push(monthIndex);
                            filteredNextDays.push(nextDay);
                            return day;
                        }
                        return false;
                    }
                );
            });

            return [
                flatten(filteredDays),
                filteredNextDays,
                filteredMonthIndexes,
            ];
        }, [calendar]);

    return (
        <>
            <div className="ab-page">
                <div className="ab-body-mobile">
                    <div className="ab-body-sidebar">
                        <div className="ab-body-main">
                            <MobileHeaderOptions propertyType={propertyType} />

                            <div className="ab-multi-results"></div>
                            <div className="ab-inner-mobile-widget">
                                <LowestRatesSlider
                                    lowestRateDays={lowestRateDays}
                                    lowestRateNextDays={lowestRateNextDays}
                                    lowestRateMonthIndexes={
                                        lowestRateMonthIndexes
                                    }
                                    Lang={Lang}
                                    countryCode={userCountryCode}
                                    onClick={onClick}
                                    cellSpacing={10}
                                    mainClass="ab-daily-lowest-rate"
                                    title="LOWEST DAILY RATE"
                                    wrapperClass="ab-lowest-daily-rates-slider-cnt ab-lowest-daily-mobile"
                                    type="daily"
                                    animation={true}
                                />
                                <div className="ab-rooms-container" ref={ref}>
                                    {(isFetchingRooms || isFetchingPrices) && (
                                        <>
                                            <SkeletonMobileRoom />
                                            <SkeletonMobileRoom />
                                            <SkeletonMobileRoom />
                                        </>
                                    )}
                                    {!isFetchingRooms &&
                                        !isFetchingPrices &&
                                        roomTypes.map((room, index) => {
                                            return (
                                                <MobileRoom
                                                    key={index}
                                                    room={room}
                                                    index={index}
                                                    isBestRate={index === 0}
                                                    property={property}
                                                />
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

MobilePropertyDetails.propTypes = {
    closeProperyDetails: PropTypes.func,
    property: PropTypes.shape({
        propertyName: PropTypes.string,
    }),
    showHeader: PropTypes.bool,
    openPropertyDetails: PropTypes.func,
    anchor: PropTypes.oneOf(['left', 'right']),
};

export default MobilePropertyDetails;
